/**
 * Component: Shared Search Surface primitives
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R10, WIDGET-V1-R11, WIDGET-V1-R12
 *
 * Small building blocks every search surface reuses: the
 * typed error-to-copy mapper, the inline error-card
 * component, and the query-form render helper. Pulling
 * these out keeps galen-search.tsx / semantic-search.tsx /
 * simple-search.tsx focused on their endpoint-specific
 * request shaping and result rendering.
 *
 * The shared render helpers are deliberately small —
 * they return JSX fragments rather than wrapping each
 * surface's entire shell — so individual surfaces retain
 * control over layout, filter placement, and the specific
 * ARIA landmarks their accessibility tier needs.
 */

import React, { useCallback, useState } from 'react';

import type { WidgetApiClient } from '../api/client';
import {
  WidgetAuthError,
  WidgetNetworkError,
  WidgetRateLimitError,
} from '../api/errors';
import { trackWidgetUsage } from '../core/usage-tracking';
import { SafeHtml } from './browse-content';

/**
 * Map a typed widget error to short, user-visible copy
 * for inline rendering inside a search surface. Long-form
 * guidance (administrator contact, retry hints) lives in
 * the shared R20 error-card component used by the init
 * pipeline; this helper gives each surface a terse
 * inline version so a post-init failure does not kick the
 * user back to the init-time error card.
 */
export function explainWidgetError(error: Error): string {
  if (error instanceof WidgetRateLimitError) {
    if (error.retryAfterSeconds !== null) {
      return `Rate limit reached — please try again in about ${error.retryAfterSeconds} seconds.`;
    }
    return 'Rate limit reached — please try again in a few minutes.';
  }
  if (error instanceof WidgetAuthError) {
    return 'Authentication expired — please refresh the page.';
  }
  if (error instanceof WidgetNetworkError) {
    return 'Galen is temporarily unreachable — please check your network and try again.';
  }
  return error.message || 'Something went wrong — please try again.';
}

/**
 * Inline error banner rendered above the query form when
 * a non-recoverable error occurred. Matches the chat
 * surface's error styling so the three surfaces feel
 * consistent.
 */
export const SurfaceErrorBanner: React.FC<{ message: string }> = ({
  message,
}) => (
  <div
    data-galen-surface-error
    role="alert"
    style={{
      padding: '0.75rem',
      border: '1px solid var(--galen-error-border, #fca5a5)',
      background: 'var(--galen-error-bg, #fef2f2)',
      color: 'var(--galen-error-fg, #991b1b)',
      borderRadius: 'var(--galen-border-radius, 0.5rem)',
      fontSize: '0.9rem',
    }}
  >
    {message}
  </div>
);

/**
 * Shared shell styles for the search surface wrapper so
 * every surface gets the same spacing + font inheritance
 * without duplicating the object literal.
 */
export const SURFACE_WRAPPER_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  fontFamily: 'var(--galen-font-family, system-ui, sans-serif)',
  color: 'var(--galen-foreground, #0f172a)',
};

/**
 * Shared styles for the primary action button across
 * surfaces so a theme-token change (e.g. primary colour)
 * propagates to all of them without a per-surface edit.
 */
export const SURFACE_PRIMARY_BUTTON_STYLE = (
  disabled: boolean,
): React.CSSProperties => ({
  marginTop: '0.5rem',
  padding: '0.5rem 1rem',
  border: 'none',
  borderRadius: 'var(--galen-border-radius, 0.5rem)',
  background: 'var(--galen-primary, #0f172a)',
  color: 'var(--galen-primary-foreground, #ffffff)',
  fontFamily: 'inherit',
  fontSize: '0.95rem',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

/**
 * Shared list container that holds search-result rows. Renders
 * a column of cards with consistent inter-row spacing so the
 * three search surfaces share a single visual rhythm without
 * each one re-declaring `<ul>` + reset styles.
 */
export const SURFACE_RESULTS_LIST_STYLE: React.CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

/**
 * Single search-result row, framed as a discrete card so
 * consecutive rows read as separate "result units" rather
 * than divider-separated paragraphs (AB#4394 follow-up,
 * user-flagged 2026-05-11). Mirrors the visual rhythm the
 * FRONTEND-WEB-V1 SearchResultCard uses (border + rounded
 * corners + light shadow + card background) without
 * importing the portal's shadcn Card primitive.
 */
export const SURFACE_RESULT_CARD_STYLE: React.CSSProperties = {
  padding: '0.75rem',
  border: '1px solid var(--galen-border, #e2e8f0)',
  borderRadius: 'var(--galen-border-radius, 0.5rem)',
  background: 'var(--galen-card, #ffffff)',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
  // Subtle hover affordance — `transition` is harmless when
  // the host page doesn't theme it. We don't apply a hover
  // background because inline styles can't carry pseudo-
  // selectors; the box-shadow alone is enough lift for the
  // user to see they're hovering an interactive row.
  transition: 'box-shadow 150ms ease-out',
};

/**
 * Strip HTML/XML tags from a string. Mirrors the
 * FRONTEND-WEB-V1 `search-result-utils.stripHtml` so widget
 * and portal extract plain text identically. Used for
 * text-only contexts (titles, subtitle metadata) — for rich
 * content rendering use SafeHtml instead.
 */
export function stripHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string') return '';
  return value.replace(/<[^>]*>/g, '').trim();
}

/**
 * Normalise smart/curly quotes to straight quotes so phrase search ("...")
 * works regardless of the keyboard, OS auto-correct, or editor that produced
 * the query. macOS/iOS/Windows smart-quotes and copy-paste from documents turn
 * `"` into `“ ”` (and `'` into `‘ ’`), which Azure Search does NOT honour as a
 * phrase delimiter — so a curly-quoted phrase silently stops narrowing results.
 */
export function normalizeQuotes(value: string): string {
  return value.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
}

/**
 * Extract a metadata field as a plain string, stripping
 * HTML and treating the upstream sentinel `"null"` (the
 * string, not the literal) as empty. Mirrors the portal
 * `search-result-utils.getMetaString` so both surfaces
 * read the same way.
 */
export function getMetaString(
  metadata: Record<string, unknown> | null | undefined,
  key: string,
): string {
  if (!metadata) return '';
  const val = metadata[key];
  if (val === null || val === undefined || val === 'null') return '';
  return stripHtml(val);
}

/**
 * Choose the row title with the same precedence the portal
 * SearchResultCard uses (AB#4394 follow-up). The Azure
 * Search index typically serialises the parent guideline
 * name into `result.title`, which collapses many
 * section-level chunks of the same guideline into visually
 * identical rows. Preferring `metadata.GuidelineSectionName`
 * differentiates rows naturally without changing the
 * backend contract.
 */
export function pickResultTitle(result: {
  title?: string | null;
  metadata?: Record<string, unknown> | null;
}): string {
  const sectionTitle = getMetaString(result.metadata, 'GuidelineSectionName');
  if (sectionTitle.length > 0) {
    return sectionTitle;
  }
  return (result.title ?? '').trim();
}

/**
 * Choose the row subtitle (the line under the title). When
 * the title is a section name, surfacing the guideline name
 * underneath gives the user the orienting context the
 * portal's SearchResultCard uses (`shortTitle ||
 * guidelineName`). The `ShortTitle` highlight contains the
 * `<em>...</em>` match markers; we strip them here because
 * the subtitle line is plain-text styled.
 */
export function pickResultSubtitle(result: {
  title?: string | null;
  metadata?: Record<string, unknown> | null;
}): string | undefined {
  // Only show a subtitle when the title is the section name;
  // otherwise the title already IS the guideline name and a
  // subtitle would just duplicate it.
  const sectionTitle = getMetaString(result.metadata, 'GuidelineSectionName');
  if (sectionTitle.length === 0) {
    return undefined;
  }
  const shortTitle = getMetaString(result.metadata, 'ShortTitle');
  if (shortTitle.length > 0) {
    return shortTitle;
  }
  const guidelineName = getMetaString(result.metadata, 'GuidelineName');
  if (guidelineName.length > 0) {
    return guidelineName;
  }
  // Last resort: the backend's `title` field (typically the
  // guideline name) when no metadata is available.
  return (result.title ?? '').trim() || undefined;
}

/**
 * Pick the best snippet HTML for a search result, walking
 * the same fallback chain the portal SearchResultCard uses
 * (captions -> highlights -> truncated content) and
 * returning raw HTML for SafeHtml to sanitise + render.
 *
 * Returning HTML instead of plain text is intentional: the
 * upstream Azure Search hits carry rich markup (paragraphs,
 * lists, tables, `<em>` match markers, HTML entities) that
 * is informative to the reader, and stripping it text-only
 * collapses lists into commas and shows entities like
 * `&gt;` as literal characters (AB#4394 regression with the
 * earlier text-only attempt — user-flagged 2026-05-11).
 *
 * The DOMPurify allow-list applied at render time strips
 * scripts / styles / event handlers but keeps p / ul / li /
 * strong / em / table / etc. so the snippet structurally
 * matches what the portal page renders.
 *
 * Highlights priority order picks the most-text-bearing
 * key first (`content` for the production esc-guidelines
 * index, `SectionText` for the mt-dli-guidelines dev
 * index, `RecommendationText` for recommendation hits,
 * `ShortTitle` as a last-resort signal).
 */
const HIGHLIGHT_KEY_PRIORITY = [
  'content',
  'SectionText',
  'RecommendationText',
  'ShortTitle',
];

export function pickResultSnippetHtml(result: {
  content?: string | null;
  captions?: string[] | null;
  highlights?: Record<string, string[]> | null;
  metadata?: Record<string, unknown> | null;
}): string | undefined {
  // 1. Semantic captions (preferred — already include
  //    `<em>` match markers).
  const captionFirst = result.captions?.[0];
  if (captionFirst && captionFirst.length > 0) {
    return captionFirst;
  }

  // 2. Highlights fragment from the upstream Azure Search
  //    hit. Walks priority keys so we get the same text the
  //    portal's `result.highlights['content']` would pick on
  //    the production index, and gracefully falls through to
  //    the section/short-title keys the dev index uses.
  const highlights = result.highlights;
  if (highlights) {
    for (const key of HIGHLIGHT_KEY_PRIORITY) {
      const arr = highlights[key];
      const first = arr?.[0];
      if (first && first.length > 0) {
        return first;
      }
    }
  }

  // 3. Fall through to result.content, then to
  //    metadata.SectionText (mirrors the portal's section-vs-
  //    recommendation fallback). Truncate to 200 chars for
  //    list compactness; SafeHtml will preserve the structural
  //    markup that remains.
  const sectionText = getMetaStringRaw(result.metadata, 'SectionText');
  const body =
    result.content && result.content.length > 0
      ? result.content
      : sectionText;
  if (body.length === 0) {
    return undefined;
  }
  return body.length > 200 ? body.substring(0, 200) + '…' : body;
}

/**
 * Raw metadata reader that does NOT strip HTML — used by
 * the snippet picker where we want to preserve markup for
 * SafeHtml. `getMetaString` is for plain-text contexts.
 */
function getMetaStringRaw(
  metadata: Record<string, unknown> | null | undefined,
  key: string,
): string {
  if (!metadata) return '';
  const val = metadata[key];
  if (val === null || val === undefined || val === 'null') return '';
  return typeof val === 'string' ? val : '';
}

/**
 * Pick the full-content HTML for a result row (AB#4395).
 * Walks the same recommendation-vs-section split the portal
 * `SearchResultCard` uses (`rawContent = isRecommendation ?
 * result.content : (sectionText || result.content)`):
 *
 *   - Recommendation hits (metadata.RecommendationId is a
 *     real id > 0) carry the full text in `result.content`.
 *   - Section hits carry the full body in
 *     `metadata.SectionText`; `result.content` is empty for
 *     these on the production indices.
 *
 * Returned HTML is unfiltered; SafeHtml sanitises at render.
 */
export function pickResultFullContentHtml(result: {
  content?: string | null;
  metadata?: Record<string, unknown> | null;
}): string | undefined {
  const recIdRaw = result.metadata?.['RecommendationId'];
  const recIdNum =
    typeof recIdRaw === 'number'
      ? recIdRaw
      : typeof recIdRaw === 'string' && recIdRaw !== 'null'
        ? Number.parseInt(recIdRaw, 10)
        : NaN;
  const isRecommendation = Number.isFinite(recIdNum) && recIdNum > 0;

  if (isRecommendation && result.content && result.content.length > 0) {
    return result.content;
  }
  const sectionText = getMetaStringRaw(result.metadata, 'SectionText');
  if (sectionText.length > 0) {
    return sectionText;
  }
  if (result.content && result.content.length > 0) {
    return result.content;
  }
  return undefined;
}

/**
 * Whether a result row has enough additional content beyond
 * its snippet to warrant a Show more / Show less toggle
 * (AB#4395). Mirrors the portal SearchResultCard rule
 * (`!isRecommendation && cleanContent.length > 200`) loosely
 * — we collapse the recommendation special-case here because
 * the widget rendering already shows the recommendation text
 * in the snippet position; what the user wants to expand is
 * the surrounding section body, when present.
 */
export function resultHasExpandableContent(result: {
  content?: string | null;
  metadata?: Record<string, unknown> | null;
}): boolean {
  const full = pickResultFullContentHtml(result);
  if (!full) return false;
  return stripHtml(full).length > 200;
}

/* ------------------------------------------------------------------ */
/* SearchResultExpander (AB#4395)                                      */
/* ------------------------------------------------------------------ */

/**
 * One result row's snippet / Show more / Show less block,
 * extracted so the three search surfaces share a single
 * expansion-state machine (AB#4395). The hook lives here so
 * each row gets its own local `isExpanded` state without the
 * three surfaces having to invent their own row sub-component.
 *
 * Usage tracking: fires `result_expand` exactly once per
 * expansion (not on collapse). Action name matches the
 * existing search_query / link_open usage events so COUNTER
 * pipeline absorbs it with no new event type. Surface key is
 * the same canonical kebab-case identifier the surfaces use
 * for `trackWidgetUsage` elsewhere.
 */
const SHOW_MORE_BUTTON_STYLE: React.CSSProperties = {
  // AB#4398: marginTop now lives on the parent footer row,
  // not the button itself, so the button can sit inline next
  // to the right-side footer slot (BrowseLink) without
  // double-spacing.
  background: 'transparent',
  border: 'none',
  padding: 0,
  color: 'var(--galen-primary, #0f172a)',
  fontFamily: 'inherit',
  fontSize: '0.8rem',
  cursor: 'pointer',
  textDecoration: 'underline',
};

/**
 * AB#4398: shared style for the result-card footer row that
 * holds the Show more / Show less toggle on the left and an
 * optional `footerRight` slot (typically the BrowseLink) on
 * the right. Renders only when at least one slot has content
 * so cards with neither stay tight.
 */
const RESULT_CARD_FOOTER_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '0.5rem',
};

const SNIPPET_STYLE: React.CSSProperties = {
  fontSize: '0.9rem',
  color: 'var(--galen-muted, #475569)',
  marginTop: '0.25rem',
};

const FULL_CONTENT_STYLE: React.CSSProperties = {
  fontSize: '0.9rem',
  color: 'var(--galen-foreground, #0f172a)',
  marginTop: '0.5rem',
  paddingTop: '0.5rem',
  borderTop: '1px solid var(--galen-border, #e2e8f0)',
};

export interface SearchResultExpanderProps {
  /**
   * The result row. Read via the shared snippet / full-
   * content / has-more helpers so each surface stays out of
   * the expansion logic.
   */
  result: {
    document_id: string;
    content?: string | null;
    captions?: string[] | null;
    highlights?: Record<string, string[]> | null;
    metadata?: Record<string, unknown> | null;
  };
  /** Surface identifier for usage tracking. */
  surfaceKey: 'galen-search' | 'semantic-search' | 'simple-search';
  /** API client; used only for the result_expand event POST. */
  client: WidgetApiClient;
  /**
   * Current query text included in the usage event payload
   * for COUNTER attribution; optional.
   */
  query?: string;
  /**
   * AB#4398: optional right-aligned slot rendered alongside
   * the Show more / Show less toggle in a single footer row.
   * Search surfaces pass the BrowseLink here so both controls
   * share one row instead of stacking vertically. Pass null
   * (or omit) when the surface has no right-side footer
   * affordance; the expander then renders the toggle on its
   * own with the footer row's spacing.
   */
  footerRight?: React.ReactNode;
}

export const SearchResultExpander: React.FC<SearchResultExpanderProps> = ({
  result,
  surfaceKey,
  client,
  query,
  footerRight,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const snippetHtml = pickResultSnippetHtml(result);
  const fullContentHtml = pickResultFullContentHtml(result);
  const hasMore = resultHasExpandableContent(result);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => {
      const next = !prev;
      if (next) {
        void trackWidgetUsage(client, surfaceKey, 'result_expand', {
          document_id: result.document_id,
          ...(query ? { query_text: query } : {}),
        });
      }
      return next;
    });
  }, [client, surfaceKey, query, result.document_id]);

  // AB#4398: render the footer row only when at least one
  // slot has content. The space-between layout keeps Show
  // more on the left and the footerRight node on the right;
  // an empty left placeholder preserves the right-edge
  // alignment when only the right slot is present.
  const hasFooterRight = footerRight !== undefined && footerRight !== null;
  const showFooterRow = hasMore || hasFooterRight;

  return (
    <>
      {!isExpanded && snippetHtml && (
        <SafeHtml html={snippetHtml} style={SNIPPET_STYLE} />
      )}
      {isExpanded && fullContentHtml && (
        <SafeHtml html={fullContentHtml} style={FULL_CONTENT_STYLE} />
      )}
      {showFooterRow && (
        <div
          data-galen-search-result-footer
          style={RESULT_CARD_FOOTER_ROW_STYLE}
        >
          {hasMore ? (
            <button
              type="button"
              onClick={handleToggle}
              data-galen-search-result-toggle
              aria-expanded={isExpanded}
              style={SHOW_MORE_BUTTON_STYLE}
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </button>
          ) : (
            // Empty left placeholder so space-between still
            // pushes the right slot to the right edge.
            <span />
          )}
          {hasFooterRight ? footerRight : null}
        </div>
      )}
    </>
  );
};
